import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";

const Appointment = () => {
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const cloudinaryImageUrl =
    "https://res.cloudinary.com/ddvpczzng/image/upload/v1757497447/Untitled_design_1_aqtynv.png";

  useEffect(() => {
    const today = new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    if (form.current) {
      form.current.date.value = today;
    }
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    emailjs
      .sendForm(
        "service_gr5mw3o",
        "template_l2a0ngg",
        form.current,
        "Na2Bf_RCujJef7MbC"
      )
      .then(
        () => {
          setSubmitSuccess(true);
          form.current.reset();
          setTimeout(() => setSubmitSuccess(false), 5000);
        },
        (error) => {
          alert("Something went wrong. Please try again.");
          console.error("EmailJS Error:", error);
        }
      )
      .finally(() => setIsSubmitting(false));
  };

  return (
    <section
      className="relative w-screen left-1/2 right-1/2 mx-[-50vw] overflow-hidden 
 -mt-12 md:-mt-16 lg:-mt-20 
 mb-[-48px] md:mb-[-64px] lg:mb-[-80px] 
 pt-16 md:pt-20 lg:pt-24 
 pb-16 md:pb-20 lg:pb-24 
 bg-pink-200"
      aria-labelledby="appointment-heading"
    >
      {/* Hidden heading so the section has an accessible name */}
      <h2 id="appointment-heading" className="sr-only">
        Schedule a Visit Appointment
      </h2>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="max-w-6xl mx-auto flex flex-col lg:flex-row bg-white shadow-xl rounded-3xl overflow-hidden"
      >
        {/* Left Image */}
        <div
          className="lg:w-1/2 w-full h-64 sm:h-80 lg:h-auto relative"
          aria-hidden="true"
        >
          <img
            src={cloudinaryImageUrl}
            alt="School campus"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            width="1600"
            height="1200"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10 pointer-events-none" />
        </div>

        {/* Right Form */}
        <div className="lg:w-1/2 w-full p-6 sm:p-8 lg:p-10 bg-gray-50">
          <h2 className="text-2xl sm:text-3xl font-bold text-cyan-800 mb-6">
            Schedule a Visit
          </h2>

          {submitSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-4 bg-green-100 text-green-800 rounded-md"
              role="status"
              aria-live="polite"
            >
              Appointment submitted successfully! We'll contact you soon.
            </motion.div>
          )}

          <form
            ref={form}
            onSubmit={sendEmail}
            aria-label="Appointment Form"
            className="space-y-4 sm:space-y-5"
          >
            <input type="hidden" name="date" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {[
                {
                  type: "text",
                  name: "guardian_name",
                  placeholder: "Parent/Guardian Name",
                  required: true,
                  autoComplete: "name",
                },
                {
                  type: "email",
                  name: "guardian_email",
                  placeholder: "Email Address",
                  required: true,
                  pattern: "[^@\\s]+@[^@\\s]+\\.[^@\\s]+",
                  autoComplete: "email",
                  inputMode: "email",
                },
                {
                  type: "tel",
                  name: "phone",
                  placeholder: "Phone Number",
                  required: true,
                  autoComplete: "tel",
                  inputMode: "tel",
                },
                {
                  type: "text",
                  name: "child_name",
                  placeholder: "Child's Name",
                  required: true,
                  autoComplete: "given-name",
                },
              ].map((field, index) => {
                const inputId = `appt-${field.name}`;
                return (
                  <div key={index}>
                    <label htmlFor={inputId} className="sr-only">
                      {field.placeholder}
                    </label>
                    <input
                      id={inputId}
                      {...field}
                      aria-required={field.required ? "true" : "false"}
                      className="w-full border border-gray-300 px-4 py-2 sm:py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-600 transition-all duration-200"
                    />
                  </div>
                );
              })}

              {/* Child Age */}
              <div>
                <label htmlFor="appt-child_age" className="sr-only">
                  Child's Age
                </label>
                <input
                  id="appt-child_age"
                  type="number"
                  name="child_age"
                  placeholder="Child's Age"
                  min="3"
                  max="18"
                  required
                  aria-required="true"
                  inputMode="numeric"
                  className="w-full border border-gray-300 px-4 py-2 sm:py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-600 transition-all duration-200"
                />
              </div>

              {/* Purpose */}
              <div>
                <label htmlFor="appt-purpose" className="sr-only">
                  Purpose of Visit
                </label>
                <select
                  id="appt-purpose"
                  name="purpose"
                  required
                  aria-required="true"
                  className="w-full border border-gray-300 px-4 py-2 sm:py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-600 bg-white appearance-none"
                >
                  <option value="">Purpose of Visit</option>
                  {[
                    "Admission Inquiry",
                    "Teacher Admission",
                    "School Tour",
                    "Meeting with Principal",
                    "Other",
                  ].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="appt-message" className="sr-only">
                Additional Information
              </label>
              <textarea
                id="appt-message"
                name="message"
                placeholder="Additional Information"
                rows="4"
                className="w-full border border-gray-300 px-4 py-2 sm:py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-600 resize-none transition-all duration-200"
              ></textarea>
            </div>

            {/* Submit */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                aria-disabled={isSubmitting ? "true" : "false"}
                aria-busy={isSubmitting ? "true" : "false"}
                className="w-full max-w-xs px-6 py-2.5 rounded-md font-semibold text-white bg-cyan-600 hover:bg-cyan-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex justify-center items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      role="img"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  "Submit Request"
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </section>
  );
};

export default React.memo(Appointment);
